import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type {
  CmmsRecordCitation,
  DocChatCmmsData,
  ResponseSegment,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { z } from 'zod'

import { decodeCmmsEntry } from './cmmsRecordEncoding'
import {
  CMMS_NUMBER_FIELDS,
  CMMS_RECORD_TYPES,
  type CmmsRecordType,
} from './cmmsRecordTypes'
import { workOrderAdditionalSchema } from './workOrderFields'
import { workOrderPlainText } from './workOrderText'

const text = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((value) =>
    value === null || value === undefined ? '' : String(value)
  )
const WorkLogSchema = z.object({
  description: text,
  description_longdescription: text,
  createdate: text,
  createby: text,
  logtype: text,
})
export const WorkOrderEntrySchema = z.object({
  entry: z.object({
    id: z.string(),
    type: z.enum(CMMS_RECORD_TYPES),
    tags: z.object({ site: z.string(), organization: z.string() }),
    data: z.object({
      source: z.object({
        system: z.string(),
        key: text,
        host: z.string(),
        site: z.string(),
        version: z.string(),
        changed_at: text,
        synced_at: text,
        deleted_at: text,
      }),
      record: z
        .object({
          wonum: text,
          description: text,
          description_longdescription: text,
          status: text,
          status_description: text,
          statusdate: text,
          worktype: text,
          woclass: text,
          woclass_description: text,
          location: text,
          assetnum: text,
          parent: text,
          istask: z.boolean().nullish(),
          wogroup: text,
          jpnum: text,
          pmnum: text,
          wopriority: text,
          reportedby: text,
          reportdate: text,
          targstartdate: text,
          targcompdate: text,
          actstart: text,
          actfinish: text,
          worklog: z
            .array(WorkLogSchema)
            .nullish()
            .transform((logs) => logs ?? []),
        })
        .extend(workOrderAdditionalSchema.shape)
        .passthrough(),
    }),
  }),
})
export type WorkOrderEntry = z.infer<typeof WorkOrderEntrySchema>['entry']

export function cmmsRecordNumber(entry: WorkOrderEntry): string {
  if (entry.type === 'cmms-work-order') return entry.data.record.wonum
  const identifier = entry.data.record[CMMS_NUMBER_FIELDS[entry.type]]
  return (typeof identifier === 'string' || typeof identifier === 'number') &&
    String(identifier)
    ? String(identifier)
    : entry.data.source.key
}

export function isCmmsRecordId(id?: string | null): boolean {
  return Boolean(
    id && CMMS_RECORD_TYPES.some((type) => id.startsWith(`${type}-`))
  )
}

export function isWorkOrderId(id?: string | null): boolean {
  return id?.startsWith('cmms-work-order-') ?? false
}

export function workOrderDocumentRow(
  citation: Pick<
    CmmsRecordCitation,
    'entry_id' | 'number' | 'title' | 'status' | 'version'
  > & { record_type?: CmmsRecordType }
): DocumentTableRow {
  return {
    id: citation.entry_id,
    systemEntryId: citation.entry_id,
    title: citation.number,
    documentTitle: citation.title,
    filename: `${citation.number}.pdf`,
    drawing: citation.number,
    system: null,
    vendor: null,
    docType: citation.record_type?.replace(/^cmms-/, '') ?? 'work-order',
    fileType: 'pdf',
    timestamp: null,
    numPages: null,
    revisionCode: null,
    revisionDate: null,
    summary: citation.status ?? null,
    keywords: [],
    cmmsVersion: citation.version,
    cmms: { number: citation.number, status: citation.status ?? '' },
  }
}

export function workOrderRowFromEntry(entry: WorkOrderEntry): DocumentTableRow {
  const row = workOrderDocumentRow({
    entry_id: entry.id,
    record_type: entry.type,
    number: cmmsRecordNumber(entry),
    title: entry.data.record.description,
    status: entry.data.record.status,
    version: entry.data.source.version,
  })
  const record = entry.data.record
  return {
    ...row,
    summary:
      workOrderPlainText(record.description_longdescription) ||
      record.worklog
        .map((log) =>
          workOrderPlainText(log.description_longdescription || log.description)
        )
        .find(Boolean) ||
      null,
    cmms: {
      number: cmmsRecordNumber(entry),
      status: record.status_description || record.status,
      assetNumber: record.assetnum,
      location: record.location,
      updatedAt: entry.data.source.changed_at,
    },
  }
}

export function citedWorkOrderRows(
  segments?: ResponseSegment[]
): DocumentTableRow[] {
  const rows = new Map<string, DocumentTableRow>()
  for (const segment of segments ?? []) {
    for (const citation of segment.citations) {
      if (citation.type === 'cmms_record' && !rows.has(citation.entry_id)) {
        rows.set(citation.entry_id, workOrderDocumentRow(citation))
      }
    }
  }
  return [...rows.values()]
}

export function parseWorkOrders(
  data: unknown,
  siteId: string,
  orgId: string
): WorkOrderEntry[] {
  if (!Array.isArray(data)) return []
  return data.flatMap((item) => {
    const parsed = WorkOrderEntrySchema.safeParse(decodeCmmsEntry(item))
    if (!parsed.success) return []
    const entry = parsed.data.entry
    if (
      entry.type === 'cmms-vendor' &&
      typeof entry.data.record.name === 'string'
    )
      entry.data.record.description = entry.data.record.name
    if (!cmmsRecordNumber(entry)) return []
    return entry.tags.site === siteId &&
      entry.tags.organization === orgId &&
      !entry.data.source.deleted_at
      ? [entry]
      : []
  })
}

export function retrievedWorkOrderRows(response: {
  cmms?: DocChatCmmsData | null
  response_segments?: ResponseSegment[]
}): DocumentTableRow[] {
  const rows = new Map<string, DocumentTableRow>()
  const excerpts = new Map<string, string>()
  for (const segment of response.response_segments ?? []) {
    for (const citation of segment.citations) {
      if (citation.type !== 'cmms_record' || citation.failure_reason) continue
      const excerpt = workOrderExcerpt(citation.quote)
      if (excerpt && !excerpts.has(citation.entry_id))
        excerpts.set(citation.entry_id, excerpt)
    }
  }
  for (const record of response.cmms?.records ?? []) {
    rows.set(record.entry_id, {
      ...workOrderDocumentRow(record),
      summary:
        excerpts.get(record.entry_id) ||
        workOrderExcerpt(record.excerpt) ||
        null,
      system: record.location ?? null,
      timestamp: record.changed_at ?? null,
      cmms: {
        number: record.number,
        status: record.status_label || record.status || '',
        assetNumber: record.asset_number,
        location: record.location,
        updatedAt: record.changed_at,
      },
    })
  }
  for (const row of citedWorkOrderRows(response.response_segments)) {
    if (!rows.has(row.id))
      rows.set(row.id, { ...row, summary: excerpts.get(row.id) || null })
  }
  return [...rows.values()]
}

export function workOrderExcerpt(text: string): string {
  return workOrderPlainText(text)
    .split('\n')
    .filter(
      (line) =>
        !/^(?:Work order \S+:|Status:|Task record \(istask\):|WO group \(wogroup\):|Job plan \(jpnum\):|Status updated \(statusdate\):|Type:|Location:|Asset:|Parent:|PM:|Reported \d{4}-|Target (?:start|finish):|Actual (?:start|finish):|Worklogs:$)/i.test(
          line
        )
    )
    .map((line) => line.replace(/^- \d{4}-\d{2}-\d{2}[^:]*:\s*/, ''))
    .filter(Boolean)
    .join(' ')
}
